import { PageDataTable } from "components/PageDataTable/PageDataTable";
import { Column } from "primereact/column";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { useNavigate } from "react-router-dom";
import { useGetSftpPartnersQuery } from "store/queries/sftpPartners";
import { ISftpPartnerSummary } from "types/SftpPartner";

import { formatDateTime } from "../../utils/hstImports";
import {
  ingestModeLabel,
  relativeTime,
  sftpPartnerPath,
} from "../../utils/sftpPartners";

export function SftpStatusTag({ enabled }: { enabled: boolean }) {
  return enabled ? (
    <Tag value="Enabled" severity="success" />
  ) : (
    <Tag value="Disabled" style={{ background: "var(--gray-500)" }} />
  );
}

const slugBody = (p: ISftpPartnerSummary) => (
  <span className="font-semibold">{p.slug}</span>
);

const accountBody = (p: ISftpPartnerSummary) =>
  p.accountName ?? <span className="text-600">{p.accountId}</span>;

const modeBody = (p: ISftpPartnerSummary) => ingestModeLabel(p.ingestMode);

const statusBody = (p: ISftpPartnerSummary) => (
  <SftpStatusTag enabled={p.enabled} />
);

const lastProcessedBody = (p: ISftpPartnerSummary) =>
  p.lastProcessedAt ? (
    <span title={formatDateTime(p.lastProcessedAt)}>
      {relativeTime(p.lastProcessedAt)}
    </span>
  ) : (
    <span className="text-600">Never</span>
  );

const prefixBody = (p: ISftpPartnerSummary) => <code>{p.s3Prefix}</code>;

// Partners whose EDI files are loaded from s3://clearhealth-sftp/<slug>/ by the daily SFTP job.
// Read-only list; a row opens the partner's page, where it can be enabled or disabled.
export function SftpPartnersTab() {
  const navigate = useNavigate();
  const { data, isFetching, isError } = useGetSftpPartnersQuery();
  const partners = data?.data || [];

  return (
    <div className="flex flex-column gap-3">
      {isError ? (
        <Message
          severity="error"
          text="Couldn't load SFTP partners. Refresh to try again."
        />
      ) : null}
      <PageDataTable
        value={partners}
        dataKey="slug"
        loading={isFetching}
        emptyMessage="No SFTP partners configured."
        onRowClick={(e) =>
          navigate(sftpPartnerPath((e.data as ISftpPartnerSummary).slug))
        }
        rowClassName={() => "cursor-pointer"}
      >
        <Column header="Partner" body={slugBody} />
        <Column header="Account" body={accountBody} />
        <Column header="Mode" body={modeBody} />
        <Column header="Status" body={statusBody} />
        <Column header="Rules" field="ruleCount" />
        <Column header="Files processed" field="filesProcessed" />
        <Column header="Last file processed" body={lastProcessedBody} />
        <Column header="S3 prefix" body={prefixBody} />
      </PageDataTable>
    </div>
  );
}
